import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useSyncExternalStore,
    type ReactNode,
} from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import SessionContext from '../../contexts/SessionContext';
import type {
    ReportingComparison,
    OverviewBundlePartialResponse,
    OverviewBundleResponse,
    OverviewBundleSection,
} from '../../types/reporting';
import {
    getOverviewBundle,
    getOverviewBundleExcludeEvsQueryKey,
    getOverviewBundleExcludeEvsSections,
} from './useOverviewBundle';
import { REPORTING_OVERVIEW_BUNDLE_LIMIT } from './reportingBundleDomainStats';
import type { WidgetSharedParams } from '../../pages/reporting/widgets/useWidgetData';
import type { WidgetConfig } from '../../pages/reporting/widgets/types';
import {
    collectAllSections,
    collectAllTimeseriesIncludeBySource,
    collectExcludeEvsSections,
    collectExcludeEvsTimeseriesIncludeBySource,
    latchSettledDisplayParams,
    mergeBaseSections,
    mergeExcludeEvsOverlay,
    missingBundleSections,
    timeseriesLacksBySource,
} from './overviewBundleSections';

/** Base `all` slice shared by every overview widget. */
export interface OverviewBundleBaseState {
    baseQuery: UseQueryResult<OverviewBundleResponse, Error>;
    allSections: OverviewBundleSection[];
    excludeEvsSections: OverviewBundleSection[];
    /**
     * Params paired with `baseQuery.data`. Trails the live page range while
     * `isPlaceholderData` so widgets render a stale screenshot, not a mix.
     */
    displayParams: WidgetSharedParams;
}

/** Lazy excludeEvs overlay — only widgets with Exclude EVs on should subscribe. */
export interface OverviewBundleOverlayState {
    excludeEvsOverlayQuery: UseQueryResult<OverviewBundlePartialResponse, Error>;
    excludeEvsSections: OverviewBundleSection[];
    /** Params paired with overlay placeholder data (same idea as base displayParams). */
    displayParams: WidgetSharedParams;
}

/** Combined view for callers that need both (e.g. domain-stats helpers). */
export interface OverviewReportingBundleState extends OverviewBundleBaseState {
    excludeEvsOverlayQuery: UseQueryResult<OverviewBundlePartialResponse, Error>;
}

const OverviewBundleBaseContext = createContext<OverviewBundleBaseState | null>(null);

type OverlayStore = {
    getSnapshot: () => OverviewBundleOverlayState | null;
    setSnapshot: (next: OverviewBundleOverlayState | null) => void;
    subscribe: (listener: () => void) => () => void;
};

function createOverlayStore(): OverlayStore {
    let snapshot: OverviewBundleOverlayState | null = null;
    const listeners = new Set<() => void>();
    return {
        getSnapshot: () => snapshot,
        setSnapshot: (next) => {
            const prev = snapshot;
            snapshot = next;
            // Skip notify when nothing consumers care about changed (avoids sibling EV redraws).
            if (
                prev
                && next
                && prev.excludeEvsOverlayQuery.data === next.excludeEvsOverlayQuery.data
                && prev.excludeEvsOverlayQuery.isPlaceholderData
                    === next.excludeEvsOverlayQuery.isPlaceholderData
                && prev.excludeEvsOverlayQuery.isFetching === next.excludeEvsOverlayQuery.isFetching
                && prev.excludeEvsOverlayQuery.isSuccess === next.excludeEvsOverlayQuery.isSuccess
                && prev.excludeEvsOverlayQuery.isPending === next.excludeEvsOverlayQuery.isPending
                && prev.excludeEvsSections === next.excludeEvsSections
                && prev.displayParams === next.displayParams
            ) {
                return;
            }
            listeners.forEach((listener) => listener());
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}

const OverviewBundleOverlayStoreContext = createContext<OverlayStore | null>(null);

const EMPTY_OVERLAY_QUERY = {
    data: undefined,
    error: null,
    isError: false,
    isPending: false,
    isLoading: false,
    isFetching: false,
    isSuccess: false,
    isPlaceholderData: false,
    status: 'pending',
} as unknown as UseQueryResult<OverviewBundlePartialResponse, Error>;

const EMPTY_DISPLAY_PARAMS: WidgetSharedParams = {
    startDate: '',
    endDate: '',
};

const EMPTY_OVERLAY_STATE: OverviewBundleOverlayState = {
    excludeEvsOverlayQuery: EMPTY_OVERLAY_QUERY,
    excludeEvsSections: [],
    displayParams: EMPTY_DISPLAY_PARAMS,
};

/** Used when no non-EV widgets need the `all` slice — treat as idle/success so EV-only loads are not blocked. */
const EMPTY_BASE_QUERY = {
    data: undefined,
    error: null,
    isError: false,
    isPending: false,
    isLoading: false,
    isFetching: false,
    isSuccess: true,
    isPlaceholderData: false,
    isFetched: true,
    status: 'success',
} as unknown as UseQueryResult<OverviewBundleResponse, Error>;

export function getOverviewBundleQueryKey(
    startDate: string,
    endDate: string,
    granularity: 'day' | 'week' | 'biweekly' | 'month',
    locationId: string | undefined,
    limit: number,
    preset: string | undefined,
    comparison: ReportingComparison | undefined,
) {
    return [
        'reporting',
        'overview',
        'bundle',
        'base',
        startDate,
        endDate,
        granularity,
        locationId,
        limit,
        preset,
        comparison,
    ] as const;
}

function buildBundleRequest(
    params: WidgetSharedParams,
    limit: number,
    locationId?: string,
) {
    const {
        startDate,
        endDate,
        granularity = 'week',
        preset,
        comparison,
    } = params;

    return {
        startDate,
        endDate,
        granularity,
        locationId: locationId ?? params.locationId,
        limit,
        preset,
        comparison,
        includeComparison: !!comparison,
    };
}

export function OverviewReportingBundleProvider({
    params,
    widgets,
    children,
    enabled = true,
}: {
    params: WidgetSharedParams;
    widgets: WidgetConfig[];
    children: ReactNode;
    /** When false, keep cache but do not fetch (inactive reporting tab). */
    enabled?: boolean;
}) {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    const overlayStoreRef = useRef<OverlayStore | null>(null);
    if (!overlayStoreRef.current) {
        overlayStoreRef.current = createOverlayStore();
    }
    const overlayStore = overlayStoreRef.current;

    const {
        startDate,
        endDate,
        granularity = 'week',
        locationId,
        preset,
        comparison,
    } = params;

    const allSectionsKey = collectAllSections(widgets).join(',');
    const allSections = useMemo(
        () => collectAllSections(widgets),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allSectionsKey],
    );
    const hasAllSections = allSections.length > 0;
    const timeseriesIncludeBySource = collectAllTimeseriesIncludeBySource(widgets);

    // Stable array identity when the set of EV sections is unchanged (e.g. 2nd ranking
    // toggles EV on while `rankings` is already requested).
    const excludeEvsSectionsKey = collectExcludeEvsSections(widgets).join(',');
    const excludeEvsSections = useMemo(
        () => collectExcludeEvsSections(widgets),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [excludeEvsSectionsKey],
    );
    const hasOverlaySections = excludeEvsSections.length > 0;
    const overlayTimeseriesIncludeBySource = collectExcludeEvsTimeseriesIncludeBySource(widgets);

    const baseKey = getOverviewBundleQueryKey(
        startDate,
        endDate,
        granularity,
        locationId,
        REPORTING_OVERVIEW_BUNDLE_LIMIT,
        preset,
        comparison,
    );
    // Range-only key — section growth merges into this cache instead of remounting it.
    const overlayKey = getOverviewBundleExcludeEvsQueryKey(
        startDate,
        endDate,
        granularity,
        locationId,
        REPORTING_OVERVIEW_BUNDLE_LIMIT,
        preset,
        comparison,
    );

    const baseRequest = buildBundleRequest(params, REPORTING_OVERVIEW_BUNDLE_LIMIT, locationId);
    const sessionReady = enabled && !!sessionContext?.sessionId && !!startDate && !!endDate;

    /**
     * Load only the `all`-slice sections non-EV widgets need. Growing the widget
     * set merges missing sections into the same range-keyed cache.
     */
    const baseQuery = useQuery({
        queryKey: baseKey,
        queryFn: async () => {
            const prev = queryClient.getQueryData<OverviewBundleResponse>(baseKey);
            const missing = missingBundleSections(prev, allSections);
            const needsTimeseriesBySourceUpgrade = timeseriesIncludeBySource
                && allSections.includes('timeseries')
                && !!prev?.timeseries
                && timeseriesLacksBySource(prev.timeseries);
            const sectionsToFetch = needsTimeseriesBySourceUpgrade
                ? Array.from(new Set<OverviewBundleSection>([...missing, 'timeseries']))
                : missing;
            if (sectionsToFetch.length === 0 && prev) {
                return {
                    ...prev,
                    sectionsOnly: true as const,
                    sections: [...allSections],
                };
            }
            const delta = await getOverviewBundle(sessionContext?.sessionId ?? '', {
                ...baseRequest,
                sections: sectionsToFetch.length > 0 ? sectionsToFetch : allSections,
                timeseriesIncludeBySource,
            });
            return mergeBaseSections(prev, delta, allSections);
        },
        enabled: sessionReady && hasAllSections,
        staleTime: 1000 * 60 * 5,
        placeholderData: (previousData) => previousData,
    });

    // Independent of the `all` slice — both cubes load in parallel so EV widgets do not
    // wait out the base fetch on a date-range change.
    const overlayReady = sessionReady && hasOverlaySections;

    /** Fetch only the missing EV sections and merge into the range-keyed overlay cache. */
    const excludeEvsOverlayQuery = useQuery({
        queryKey: overlayKey,
        queryFn: async () => {
            const prev = queryClient.getQueryData<OverviewBundlePartialResponse>(overlayKey);
            const missing = missingBundleSections(prev, excludeEvsSections);
            const needsTimeseriesBySourceUpgrade = overlayTimeseriesIncludeBySource
                && excludeEvsSections.includes('timeseries')
                && !!prev?.timeseries
                && timeseriesLacksBySource(prev.timeseries);
            const sectionsToFetch = needsTimeseriesBySourceUpgrade
                ? Array.from(new Set<OverviewBundleSection>([...missing, 'timeseries']))
                : missing;
            if (sectionsToFetch.length === 0 && prev) {
                return {
                    ...prev,
                    excludeEvsSections: [...excludeEvsSections],
                };
            }
            const delta = await getOverviewBundleExcludeEvsSections(sessionContext?.sessionId ?? '', {
                ...baseRequest,
                excludeEvsSectionsOnly: true,
                excludeEvsSections: sectionsToFetch.length > 0 ? sectionsToFetch : excludeEvsSections,
                timeseriesIncludeBySource: overlayTimeseriesIncludeBySource,
            });
            return mergeExcludeEvsOverlay(prev, delta, excludeEvsSections);
        },
        enabled: overlayReady,
        staleTime: 1000 * 60 * 5,
        // Last range's EV slice so widgets fade it instead of skeletoning. Consumers must
        // gate on isPlaceholderData — cross-range data is display-only, never "loaded".
        placeholderData: (previousData) => previousData,
    });

    /** Cross-range placeholder must not count as cached for this range. */
    const overlayFreshData = excludeEvsOverlayQuery.isPlaceholderData
        ? undefined
        : excludeEvsOverlayQuery.data;

    // Same range, section set grew / timeseries marketplace upgrade: refill overlay cache.
    const missingOverlaySectionsKey = missingBundleSections(
        overlayFreshData,
        excludeEvsSections,
    ).join(',');
    const overlayTimeseriesNeedsBySource = overlayTimeseriesIncludeBySource
        && excludeEvsSections.includes('timeseries')
        && timeseriesLacksBySource(overlayFreshData?.timeseries);

    useEffect(() => {
        if (!overlayReady) return;
        // Initial empty cache is loaded by useQuery; only refill when something is already cached.
        if (!overlayFreshData) return;
        if (!missingOverlaySectionsKey && !overlayTimeseriesNeedsBySource) return;
        void queryClient.refetchQueries({ queryKey: overlayKey });
    }, [
        overlayReady,
        missingOverlaySectionsKey,
        overlayTimeseriesNeedsBySource,
        overlayFreshData,
        overlayKey,
        queryClient,
    ]);

    const baseFreshData = baseQuery.isPlaceholderData ? undefined : baseQuery.data;
    const missingAllSectionsKey = missingBundleSections(
        baseFreshData,
        allSections,
    ).join(',');
    const baseTimeseriesNeedsBySource = timeseriesIncludeBySource
        && allSections.includes('timeseries')
        && timeseriesLacksBySource(baseFreshData?.timeseries);

    useEffect(() => {
        if (!sessionReady || !hasAllSections) return;
        if (!baseFreshData) return;
        // Section growth or timeseries marketplace upgrade on the same range key.
        if (!missingAllSectionsKey && !baseTimeseriesNeedsBySource) return;
        void queryClient.refetchQueries({ queryKey: baseKey });
    }, [
        sessionReady,
        hasAllSections,
        missingAllSectionsKey,
        baseTimeseriesNeedsBySource,
        baseFreshData,
        baseKey,
        queryClient,
    ]);

    const effectiveBaseQuery = hasAllSections ? baseQuery : EMPTY_BASE_QUERY;

    // Atomic snapshot: last-settled page params stay glued to placeholder bundle rows.
    const settledBaseParamsRef = useRef(params);
    const baseDisplayParams = latchSettledDisplayParams(
        settledBaseParamsRef,
        params,
        hasAllSections && baseQuery.isPlaceholderData,
    );

    const settledOverlayParamsRef = useRef(params);
    const overlayDisplayParams = latchSettledDisplayParams(
        settledOverlayParamsRef,
        params,
        hasOverlaySections && excludeEvsOverlayQuery.isPlaceholderData,
    );

    const baseValue = useMemo((): OverviewBundleBaseState => ({
        baseQuery: effectiveBaseQuery,
        allSections,
        excludeEvsSections,
        displayParams: baseDisplayParams,
    }), [effectiveBaseQuery, allSections, excludeEvsSections, baseDisplayParams]);

    const overlaySnapshot = useMemo((): OverviewBundleOverlayState => ({
        excludeEvsOverlayQuery,
        excludeEvsSections,
        displayParams: overlayDisplayParams,
    }), [excludeEvsOverlayQuery, excludeEvsSections, overlayDisplayParams]);

    useEffect(() => {
        overlayStore.setSnapshot(overlaySnapshot);
    }, [overlayStore, overlaySnapshot]);

    return (
        <OverviewBundleBaseContext.Provider value={baseValue}>
            <OverviewBundleOverlayStoreContext.Provider value={overlayStore}>
                {children}
            </OverviewBundleOverlayStoreContext.Provider>
        </OverviewBundleBaseContext.Provider>
    );
}

export function useOverviewBundleBase(): OverviewBundleBaseState | null {
    return useContext(OverviewBundleBaseContext);
}

/**
 * Subscribe to the EV overlay store only when `enabled` is true.
 * Non-EV widgets pass false so overlay fetch ticks do not re-render them.
 */
export function useOverviewBundleOverlay(enabled: boolean): OverviewBundleOverlayState | null {
    const store = useContext(OverviewBundleOverlayStoreContext);
    return useSyncExternalStore(
        (onStoreChange) => {
            if (!enabled || !store) return () => {};
            return store.subscribe(onStoreChange);
        },
        () => {
            if (!enabled || !store) return null;
            return store.getSnapshot() ?? EMPTY_OVERLAY_STATE;
        },
        () => (enabled ? EMPTY_OVERLAY_STATE : null),
    );
}

/** Combined base + overlay (always subscribes to both). */
export function useOverviewReportingBundle(): OverviewReportingBundleState | null {
    const base = useOverviewBundleBase();
    const overlay = useOverviewBundleOverlay(true);
    return useMemo(() => {
        if (!base) return null;
        return {
            baseQuery: base.baseQuery,
            allSections: base.allSections,
            excludeEvsSections: base.excludeEvsSections,
            displayParams: base.displayParams,
            excludeEvsOverlayQuery: overlay?.excludeEvsOverlayQuery ?? EMPTY_OVERLAY_QUERY,
        };
    }, [base, overlay]);
}
